import React, { useState, useEffect } from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Shuffle, Repeat1, Library } from "lucide-react";
import { Badge } from "@/components/ui/badge";

import { CaretSortIcon, CheckIcon } from "@radix-ui/react-icons";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import TooltipWrapper from "@/components/TooltipWrapper";

import UserService from "@/services/UserService";

import useStore from "@/store";

import "/node_modules/flag-icons/css/flag-icons.min.css";
import { getFlagForLanguage } from "@/utils/languageFlags";
import { countryForLanguage } from "@/constants/langcraftLanguages";

const SessionControls = ({ mode, setMode }) => {
  const [open, setOpen] = React.useState(false);
  const [isChromeDesktop, setIsChromeDesktop] = useState(false);

  const { user, setCurrentCollectionId } = useStore().user;
  const { collections, currentCollection } = useStore().collections;
  const { settings, updateSettings } = useStore().settings;
  const [isMobile, setIsMobile] = useState(false);
  const { setDisableShortcuts } = useStore().learningSessionConfig;
  const selectedCollection = collections.find(
    (item) => item.id === user.current_collection_id
  );

  useEffect(() => {
    setDisableShortcuts(open);
  }, [open]);

  React.useEffect(() => {
    const down = (e) => {
      if (e.key === "c" && !e.ctrlKey && !e.metaKey && !open) {
        e.preventDefault();
        setOpen(true);
      }
      if (e.key === "Escape") {
        e.preventDefault();
        setOpen(false);
      }
    };

    document.addEventListener("keydown", down);
    return () => document.removeEventListener("keydown", down);
  }, [setOpen]);

  useEffect(() => {
    const userAgent = navigator.userAgent;
    const isChrome = /Chrome/i.test(userAgent);
    const isDesktop =
      !/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        userAgent
      );
    setIsMobile(!isDesktop);
    setIsChromeDesktop(isChrome && isDesktop);
  }, []);

  function mapLanguageToCountry(language) {
    // Default mapping for 'en' to 'gb'
    if (language === "en") {
      return "gb";
    }

    // Specific mappings for variations of English
    switch (language) {
      case "ja":
        return "jp";
      case "hi":
        return "in";
      case "en-us":
        return "us";
      case "en-gb":
      case "en-gb":
        return "gb";
      case "zh":
        return "cn";
      case "ar":
        return "sa";
      case "ko":
        return "kr";
      default:
        // Otherwise use the flag country from the LangCraft language list
        return countryForLanguage(language) || language.split("-")[0];
    }
  }

  const hasSelectedCollection =
    Boolean(user.current_collection_id) && Boolean(selectedCollection);
  const isCompactWidth =
    typeof window !== "undefined" ? window.innerWidth < 768 : false;
  const selectedTitle = selectedCollection?.title ?? "";
  const truncatedTitle =
    selectedTitle.length > 24 ? `${selectedTitle.slice(0, 24)}...` : selectedTitle;
  const displaySelectedTitle = isCompactWidth ? truncatedTitle : selectedTitle;

  return (
    <div className="relative flex w-full flex-wrap items-center justify-between gap-4 px-4 py-4">
      <div className="flex flex-1 min-w-[220px] items-center gap-3">
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              role="combobox"
              aria-expanded={open}
              className="w-full justify-between rounded-2xl border border-line bg-surface/80 px-4 py-2 text-sm font-semibold text-fg shadow-none transition hover:border-primary/40 hover:bg-elevated focus-visible:ring-2 focus-visible:ring-primary/60"
              onFocus={(e) => e.target.blur()}
            >
              {hasSelectedCollection ? (
                <span className="flex min-w-0 flex-1 items-center gap-2">
                  <span
                    aria-label={`${selectedCollection.language || "unknown"} language flag`}
                    title={
                      selectedCollection.language
                        ? `${selectedCollection.language} flag`
                        : "Language flag"
                    }
                    className="shrink-0 text-lg leading-none"
                  >
                    {getFlagForLanguage(selectedCollection.language)}
                  </span>
                  <span className="truncate">{displaySelectedTitle}</span>
                </span>
              ) : (
                "Select collection"
              )}
              <CaretSortIcon className="ml-2 h-4 w-4 shrink-0 opacity-60" />
            </Button>
          </PopoverTrigger>
          <PopoverContent
            align="start"
            className="w-[260px] rounded-3xl border-none bg-surface p-0 text-fg shadow-panel backdrop-blur-2xl"
          >
            <Command>
              <CommandInput placeholder="Search collections..." className="h-9" />
              <CommandEmpty>No collection found.</CommandEmpty>
              <CommandGroup className="max-h-[55vh] overflow-y-auto">
                {collections.map((collection) => (
                  <CommandItem
                    key={collection.id}
                    value={collection.title}
                    onSelect={() => {
                      setCurrentCollectionId(collection.id);
                      UserService.updateCurrentCollection(collection.id);
                      setOpen(false);
                    }}
                    className="flex items-center gap-3"
                  >
                    <span className="flex min-w-0 flex-1 items-center gap-2">
                      <span
                        aria-label={`${collection.language || "unknown"} language flag`}
                        title={
                          collection.language
                            ? `${collection.language} flag`
                            : "Language flag"
                        }
                        className="shrink-0 text-lg leading-none"
                      >
                        {getFlagForLanguage(collection.language)}
                      </span>
                      <span className="truncate">{collection.title}</span>
                    </span>
                    {collection.content_type === "youtube" && (
                      <span className="rounded-full bg-primary/10 px-1.5 py-[0.1rem] text-[9px] font-semibold uppercase tracking-[0.22em] text-primary/80">
                        #video
                      </span>
                    )}
                    <CheckIcon
                      className={cn(
                        "ml-2 h-4 w-4",
                        user.current_collection_id === collection.id
                          ? "opacity-100"
                          : "opacity-0"
                      )}
                    />
                  </CommandItem>
                ))}
              </CommandGroup>
            </Command>
          </PopoverContent>
        </Popover>
      </div>

      <div className="flex flex-wrap items-center justify-end gap-3">
        <Badge className="rounded-full border border-line bg-surface/80 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.3em] text-fg shadow-none">
          {currentCollection.language.toUpperCase()}
        </Badge>
        {!isMobile && (
          <Select defaultValue={mode} onValueChange={setMode}>
            <SelectTrigger
              className="w-[140px] rounded-2xl border border-line bg-surface/80 px-4 py-2 text-sm font-semibold text-fg shadow-none transition hover:border-primary/40 hover:bg-elevated focus-visible:ring-primary/60"
              onFocus={(e) => e.target.blur()}
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="w-[140px] rounded-2xl border-none bg-surface text-fg shadow-panel">
              <SelectItem value="realtime">Real-time</SelectItem>
              <SelectItem value="phoneme">Phoneme</SelectItem>
            </SelectContent>
          </Select>
        )}
      </div>
    </div>
  );
};

export default SessionControls;
